/// Developer-only announcement composer (BACKLOG #43).
///
/// Lives on the dashboard, which is already gated by DASHBOARD_ADMIN_EMAILS in
/// App.tsx — and the callable it posts to re-checks the same allowlist
/// server-side, so the gate does not depend on the UI being hidden.
///
/// Sends either a BROADCAST (everyone / a segment) or to ONE player by uid.
/// The client cannot write Firestore directly for either — rules forbid it —
/// so everything goes through `publishAnnouncement`.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { httpsCallable } from 'firebase/functions'
import { firebaseFunctions } from '../firebase/app'

const LOCALES = ['en', 'de', 'es', 'fr', 'it'] as const
type Locale = (typeof LOCALES)[number]
type LocalizedMap = Partial<Record<Locale, string>>

type Mode = 'broadcast' | 'individual'
type AudienceType = 'all' | 'segment'
type Priority = 'normal' | 'high'

interface PublishResult {
  path: string
  mode: Mode
}

const callPublish = httpsCallable<Record<string, unknown>, PublishResult>(
  firebaseFunctions,
  'publishAnnouncement',
)
const callDelete = httpsCallable<{ path: string }, { deleted: boolean }>(
  firebaseFunctions,
  'deleteAnnouncement',
)

/// What is live right now. Before this existed the composer could only delete
/// the card it had just sent, from in-memory state, so a page reload put every
/// live announcement out of reach - which is how three stale 3.2 cards stayed
/// on Tower Home for a week.
interface LiveRow {
  id: string
  path: string
  titleEn: string
  bodyEn: string
  priority: string
  audience: string
  expiresAtMs: number | null
  createdAtMs: number | null
  neverExpires: boolean
  expired: boolean
}
const callList = httpsCallable<Record<string, never>, { rows: LiveRow[] }>(
  firebaseFunctions,
  'listAnnouncements',
)

export function AnnouncementComposer() {
  const [mode, setMode] = useState<Mode>('broadcast')
  const [uid, setUid] = useState('')
  const [audienceType, setAudienceType] = useState<AudienceType>('all')
  const [segment, setSegment] = useState('buyers')
  const [playedLevel, setPlayedLevel] = useState('')
  const [minAppVersion, setMinAppVersion] = useState('')
  const [priority, setPriority] = useState<Priority>('normal')
  const [locale, setLocale] = useState<Locale>('en')
  const [title, setTitle] = useState<LocalizedMap>({})
  const [body, setBody] = useState<LocalizedMap>({})
  const [actionLabel, setActionLabel] = useState<LocalizedMap>({})
  const [actionUrl, setActionUrl] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState<PublishResult | null>(null)
  const [live, setLive] = useState<LiveRow[] | null>(null)
  const [liveError, setLiveError] = useState<string | null>(null)

  const resolvedSegment = segment === 'playedLevel' ? `playedLevel:${playedLevel.trim()}` : segment

  // Mirror of the server's hard requirement, surfaced before the round-trip:
  // EN is every other locale's fallback, so a doc without it can render blank.
  const missingEnglish = !title.en?.trim() || !body.en?.trim()
  // Mirrors the server rule. Nothing retires a card on its own, so an
  // announcement without a date stays up until a human notices.
  const missingExpiry = !expiresAt
  const canSend =
    !busy &&
    !missingEnglish &&
    !missingExpiry &&
    (mode === 'individual' ? uid.trim().length > 0 : true) &&
    (mode === 'broadcast' && audienceType === 'segment' && segment === 'playedLevel'
      ? playedLevel.trim().length > 0
      : true)

  const localesFilled = useMemo(
    () => LOCALES.filter((l) => title[l]?.trim() && body[l]?.trim()),
    [title, body],
  )

  async function send() {
    setBusy(true)
    setError(null)
    setSent(null)
    try {
      const res = await callPublish({
        mode,
        uid: mode === 'individual' ? uid.trim() : undefined,
        title,
        body,
        actionLabel: actionUrl.trim() ? actionLabel : undefined,
        actionUrl: actionUrl.trim() || undefined,
        audienceType: mode === 'broadcast' ? audienceType : undefined,
        segment: mode === 'broadcast' && audienceType === 'segment' ? resolvedSegment : undefined,
        minAppVersion: minAppVersion.trim() || undefined,
        priority,
        expiresAtMs: expiresAt ? new Date(expiresAt).getTime() : undefined,
      })
      setSent(res.data)
      void refreshLive()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const refreshLive = useCallback(async () => {
    setLiveError(null)
    try {
      const res = await callList({})
      setLive(res.data.rows)
    } catch (e) {
      setLiveError(e instanceof Error ? e.message : String(e))
    }
  }, [])

  useEffect(() => {
    void refreshLive()
  }, [refreshLive])

  /// Delete any live announcement, not just the one just sent.
  async function remove(path: string) {
    setBusy(true)
    setError(null)
    try {
      await callDelete({ path })
      await refreshLive()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function undo(path: string) {
    setBusy(true)
    try {
      await callDelete({ path })
      setSent(null)
      await refreshLive()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const input =
    'w-full rounded-md bg-slate-950 px-3 py-2 text-sm text-slate-100 ring-1 ring-white/10 placeholder:text-slate-600'
  const chip = (on: boolean) =>
    `rounded-md px-3 py-1.5 text-xs font-semibold ring-1 transition ${
      on ? 'bg-sky-600 text-white ring-sky-400' : 'bg-slate-800 text-slate-300 ring-white/10 hover:bg-slate-700'
    }`

  return (
    <section className="rounded-xl bg-slate-900/70 p-4 ring-1 ring-white/10">
      <header className="mb-3 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-slate-100">Announcements</h2>
        <span className="text-xs text-slate-500">Developer only</span>
      </header>

      {/* What is live right now. This is the half that was missing: the
          composer could only undo its own last send, so nothing in the UI
          could reach an announcement after a reload. */}
      <div className="mb-4 rounded-lg bg-slate-950/60 p-3 ring-1 ring-white/5">
        <div className="mb-2 flex items-baseline justify-between">
          <h3 className="text-xs font-semibold text-slate-300">On Tower Home now</h3>
          <button className="text-xs text-sky-400 hover:underline" onClick={() => void refreshLive()}>
            refresh
          </button>
        </div>
        {liveError ? (
          <p className="text-xs text-rose-300">{liveError}</p>
        ) : live === null ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : live.length === 0 ? (
          <p className="text-xs text-slate-500">Nothing published.</p>
        ) : (
          <ul className="space-y-2">
            {live.map((r) => (
              <li key={r.id} className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-xs text-slate-200">
                    {r.priority === 'high' && (
                      <span className="mr-1 rounded bg-sky-900/60 px-1 text-[10px] text-sky-300">TOP</span>
                    )}
                    {r.titleEn || <span className="text-slate-500">(no English title)</span>}
                  </p>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    {r.audience}
                    {' · '}
                    {r.expired ? (
                      <span className="text-slate-600">expired, no longer shown</span>
                    ) : r.neverExpires ? (
                      <span className="text-amber-400">never expires — it stays until deleted</span>
                    ) : (
                      <>expires {new Date(r.expiresAtMs as number).toISOString().slice(0, 10)}</>
                    )}
                  </p>
                </div>
                <button
                  onClick={() => void remove(r.path)}
                  disabled={busy}
                  className="shrink-0 rounded-md px-2 py-1 text-[11px] text-rose-300 ring-1 ring-rose-400/30 hover:bg-rose-500/10 disabled:opacity-40"
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Who */}
      <div className="mb-3 flex flex-wrap gap-2">
        <button className={chip(mode === 'broadcast')} onClick={() => setMode('broadcast')}>
          Everyone / segment
        </button>
        <button className={chip(mode === 'individual')} onClick={() => setMode('individual')}>
          One player
        </button>
      </div>

      {mode === 'individual' ? (
        <div className="mb-3">
          <label className="mb-1 block text-xs text-slate-400">
            Player uid — from <code>webFeedbackOwners/&#123;postId&#125;.ownerUid</code>
          </label>
          <input className={input} value={uid} onChange={(e) => setUid(e.target.value)}
                 placeholder="98tYuxsOE1POhZvHQ6LxD3I3e1S2" />
          <p className="mt-1 text-xs text-slate-500">
            Goes to that player&rsquo;s own inbox — nothing is readable by anyone else.
          </p>
        </div>
      ) : (
        <div className="mb-3 space-y-2">
          <div className="flex flex-wrap gap-2">
            <button className={chip(audienceType === 'all')} onClick={() => setAudienceType('all')}>
              Everyone
            </button>
            <button className={chip(audienceType === 'segment')} onClick={() => setAudienceType('segment')}>
              Segment
            </button>
          </div>
          {audienceType === 'segment' && (
            <div className="flex flex-wrap items-center gap-2">
              <select className={`${input} max-w-[200px]`} value={segment}
                      onChange={(e) => setSegment(e.target.value)}>
                <option value="buyers">buyers</option>
                <option value="free">free</option>
                <option value="playedLevel">played a level…</option>
              </select>
              {segment === 'playedLevel' && (
                <input className={`${input} max-w-[200px]`} value={playedLevel}
                       onChange={(e) => setPlayedLevel(e.target.value)} placeholder="WSSS" />
              )}
            </div>
          )}
          <p className="text-xs text-slate-500">
            Only <code>buyers</code>, <code>free</code> and <code>playedLevel:</code> resolve on device.
            A uid list is never used here — every player downloads this collection.
          </p>
        </div>
      )}

      {/* Copy */}
      <div className="mb-2 flex flex-wrap gap-1">
        {LOCALES.map((l) => (
          <button key={l} className={chip(locale === l)} onClick={() => setLocale(l)}>
            {l}
            {localesFilled.includes(l) ? ' ✓' : l === 'en' ? ' *' : ''}
          </button>
        ))}
      </div>
      <div className="mb-3 space-y-2">
        <input className={input} placeholder={`Title (${locale})`} value={title[locale] ?? ''}
               onChange={(e) => setTitle({ ...title, [locale]: e.target.value })} />
        <textarea className={`${input} h-20`} placeholder={`Body (${locale})`} value={body[locale] ?? ''}
                  onChange={(e) => setBody({ ...body, [locale]: e.target.value })} />
        {missingEnglish && (
          <p className="text-xs text-amber-400">
            English title and body are required — every other locale falls back to them.
          </p>
        )}
      </div>

      {/* Action */}
      <div className="mb-3 grid gap-2 sm:grid-cols-2">
        <input className={input} placeholder={`Button label (${locale})`} value={actionLabel[locale] ?? ''}
               onChange={(e) => setActionLabel({ ...actionLabel, [locale]: e.target.value })} />
        <input className={input} placeholder="flatoutatc://seasons or https://…" value={actionUrl}
               onChange={(e) => setActionUrl(e.target.value)} />
      </div>

      {/* Delivery */}
      <div className="mb-3 grid gap-2 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs text-slate-400">Priority</label>
          <div className="flex gap-2">
            <button className={chip(priority === 'normal')} onClick={() => setPriority('normal')}>
              Quiet
            </button>
            <button className={chip(priority === 'high')} onClick={() => setPriority('high')}>
              Interrupt
            </button>
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-400">Min app version</label>
          <input className={input} placeholder="3.1" value={minAppVersion}
                 onChange={(e) => setMinAppVersion(e.target.value)} />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-400">
            Expires <span className="text-rose-400">*</span>
          </label>
          <input
            className={`${input} ${missingExpiry ? 'ring-rose-500/50' : ''}`}
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
          {missingExpiry && (
            <p className="mt-1 text-[11px] text-rose-300">
              Required. Nothing retires a card on its own.
            </p>
          )}
        </div>
      </div>
      <p className="mb-3 text-xs text-slate-500">
        <strong className="text-slate-400">Quiet</strong> shows on the Tower Home card only.{' '}
        <strong className="text-slate-400">Interrupt</strong> also raises the end-of-shift banner.
        A developer reply always outranks an announcement on both.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={send}
          disabled={!canSend}
          className="rounded-md bg-sky-600 px-4 py-2 text-sm font-semibold text-white ring-1 ring-sky-400 hover:bg-sky-500 disabled:opacity-40"
        >
          {busy ? 'Sending…' : mode === 'individual' ? 'Send to player' : 'Publish'}
        </button>
        {sent && (
          <span className="text-xs text-emerald-300">
            Sent → <code>{sent.path}</code>{' '}
            <button className="underline hover:text-emerald-200" onClick={() => undo(sent.path)}>
              undo
            </button>
          </span>
        )}
        {error && <span className="text-xs text-rose-300">{error}</span>}
      </div>
    </section>
  )
}
